import { useState, useEffect, useMemo } from "react";
import { useUpdateNoteMutation, useDeleteNoteMutation } from "./notesApiSlice";
import { useParams, useNavigate } from "react-router-dom";

// small helper: ensure value fits <input type="date">
const toDateInput = (d) => {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toISOString().slice(0, 10);
};

export default function EditNoteForm({ note }) {
    const { id: routeId } = useParams(); // /dash/notes/:id
    const navigate = useNavigate();
    const targetId = note?._id || routeId;

    const [updateNote, { isLoading, isSuccess, isError, error }] = useUpdateNoteMutation();
    const [deleteNote, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
        useDeleteNoteMutation();

    // --- form state (prefill from note) ---
    const [title, setTitle] = useState(note.title || "");
    const [shortTitle, setShortTitle] = useState(note?.shortTitle || "");
    const [description, setDescription] = useState(note?.description || "");
    const [text, setText] = useState(note?.text || "");
    const [completed, setCompleted] = useState(note?.completed || false);
    const [status, setStatus] = useState(note?.status || "open");

    // Validation flags
    const validTitle = title.trim().length >= 3 && title.trim().length <= 80;
    const validShort = shortTitle.trim().length >= 3 && shortTitle.trim().length <= 60;
    const validDesc = description.trim().length > 0;
    const validText = text.trim().length > 0;

    const canSave = validTitle && validShort && validDesc && validText && !!targetId && !isLoading;


    // After save/delete → go back
    useEffect(() => {
        if (isSuccess || isDelSuccess) {
        navigate("/dash/notes");
        }
    }, [isSuccess, isDelSuccess, navigate]);

    const onSave = async (e) => {
        e.preventDefault();
        if (!canSave) return;
        try {
        await updateNote({
            id: targetId,
            title: title.trim(),
            shortTitle: shortTitle.trim(),
            description: description.trim(),
            text: text.trim(),
            completed,
            status,
        }).unwrap();
        } catch {
        // handled by error banner
        }
    };

    const onDelete = async () => {
        if (!targetId) return;
        if (!window.confirm(`Delete note "${title}"?`)) return;
        try {
        await deleteNote({id: targetId}).unwrap(); // slice uses DELETE /notes/:id
        } catch {
        // handled by error banner
        }
    };

    const errText = (error?.data?.message || delerror?.data?.message) ?? "";

    return (
        <div className="min-h-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header lives in the wrapper (EditNote.js) for consistency */}

        {(isError || isDelError) && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
            {errText || "Something went wrong."}
            </div>
        )}

        <form onSubmit={(e) => e.preventDefault()} className="mt-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Title <span className="text-gray-400 font-normal">[3–80 chars]</span>
                </label>
                <input
                    id="title"
                    type="text"
                    autoComplete="off"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validTitle ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                    placeholder="e.g. 2025 SDG Financing Note"
                />
                {!validTitle && (
                    <p className="mt-1 text-xs text-amber-600">3–80 characters.</p>
                )}
                </div>

                {/* Short Title */}
                <div>
                <label htmlFor="shorttitle" className="block text-sm font-medium text-gray-700">
                    Short Title <span className="text-gray-400 font-normal">[3–60 chars]</span>
                </label>
                <input
                    id="shorttitle"
                    type="text"
                    autoComplete="off"
                    value={shortTitle}
                    onChange={(e) => setShortTitle(e.target.value)}
                    className={`mt-1 w-full rounded-xl border ${
                    validShort ? "border-gray-200" : "border-amber-300"
                    } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                    placeholder="e.g. SDG 2025"
                />
                {!validShort && (
                    <p className="mt-1 text-xs text-amber-600">3–60 characters.</p>
                )}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description
                    </label>
                    <textarea
                        id="description"
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className={`mt-1 w-full rounded-xl border ${
                        validDesc ? "border-gray-200" : "border-amber-300"
                        } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                        placeholder="What is this note about?"
                    />
                    {!validDesc && (
                        <p className="mt-1 text-xs text-amber-600">Description is required.</p>
                    )}
                </div>

                {/* text */}
                <div className="md:col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Text
                    </label>
                    <textarea
                        id="text"
                        rows={4}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className={`mt-1 w-full rounded-xl border ${
                        validText ? "border-gray-200" : "border-amber-300"
                        } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                        placeholder="What is this note about?"
                    />
                    {!validText && (
                        <p className="mt-1 text-xs text-amber-600">Text is required.</p>
                    )}
                </div>

                {/* Completed */}
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        Completed
                    </label>
                    <select
                        id="completed"
                        value={completed}
                        onChange={(e) => setCompleted(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
                    >
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>

                {/* Status */}
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        Status
                    </label>
                    <select
                        id="status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
                    >
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>
            </div>

            {/* Footer actions */}
            <div className="mt-6 flex items-center justify-end gap-2">
                <button
                type="button"
                onClick={onDelete}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                Delete
                </button>
                <button
                type="submit"
                onClick={onSave}
                disabled={!canSave}
                className={`rounded-xl px-4 py-2 text-sm text-white ${
                    canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
                }`}
                >
                Save
                </button>
            </div>
            </div>
        </form>
        </div>
    );
}
